import React, { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Wrapper } from "./styles";
import { CalendarTodayOutlined } from "@mui/icons-material";
import { TextField } from "@mui/material";

interface DatepickerProps {
  required?: boolean;
  start: Date | null;
  end?: Date | null;
  label?: string;
  width?: string;
  size?: string;
  dateFormat?: string;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
  selectedDate?: Date | null;
  selectsRange: boolean;
  onChangeCallback: (start: string | null, end?: string) => void;
  variant?: string;
  className?: string;
  position?: string;
  portalId?: string;
  isClearable?: boolean;
  disabled?: boolean;
  showYearPicker?: boolean;
}

const Input = forwardRef<HTMLInputElement, any>(
  (
    { label, required, size, width, variant, className, disabled, ...props },
    ref,
  ) => (
    <TextField
      disabled={disabled}
      inputRef={ref}
      fullWidth
      autoComplete="off"
      inputMode="none"
      label={
        <span>
          {label}
          {required ? (
            <span style={{ color: "#d32f2f", marginLeft: 2 }}>*</span>
          ) : null}
        </span>
      }
      variant={variant}
      size={(size ?? "small") as "small" | "medium"}
      sx={{
        width: width ?? "250px",
        flex: "1",
      }}
      className={className}
      slotProps={{
        input: {
          endAdornment:
            variant === "outlined" ? (
              <CalendarTodayOutlined
                sx={{
                  cursor: "pointer",
                  marginRight: 1,
                  color: "rgba(0, 0, 0, 0.54)",
                }}
              />
            ) : null,
        },
      }}
      {...props}
    />
  ),
);

const Datepicker: React.FC<DatepickerProps> = ({
  required,
  start,
  end,
  label,
  width,
  size,
  minDate,
  maxDate,
  dateFormat,
  selectsRange,
  onChangeCallback,
  variant = "outlined",
  className = "",
  position = "bottom",
  portalId,
  isClearable = false,
  disabled = false,
  showYearPicker = false,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(start);
  const [selectedDates, setSelectedDates] = useState<
    [Date | null, Date | null]
  >([start, end || null]);

  useEffect(() => {
    setSelectedDate(start);
    setSelectedDates([start, end || null]);
  }, [start, end]);

  const handleChange = (date: Date | null | [Date | null, Date | null]) => {
    if (date === null) {
      setSelectedDate(null);
      setSelectedDates([null, null]);
      onChangeCallback(null);
      return;
    }
    if (selectsRange && Array.isArray(date)) {
      setSelectedDates(date);
      const [newStart, newEnd] = date;
      if (newStart && newEnd) {
        onChangeCallback(newStart.toISOString(), newEnd.toISOString());
      } else {
        onChangeCallback(null);
      }
    } else if (date instanceof Date) {
      setSelectedDate(date);
      onChangeCallback(date.toISOString());
    }
  };
  return (
    <Wrapper>
      <DatePicker
        disabled={disabled}
        isClearable={disabled ? false : isClearable}
        required={required}
        dateFormat={dateFormat ?? "dd/MM/yyyy"}
        selectsRange={selectsRange || undefined}
        selected={!selectsRange ? selectedDate : undefined}
        startDate={selectsRange ? selectedDates[0] : undefined}
        endDate={selectsRange ? selectedDates[1] : undefined}
        shouldCloseOnSelect={true}
        minDate={minDate}
        maxDate={maxDate}
        customInput={
          <Input
            required={required}
            label={label}
            size={size}
            width={width}
            variant={variant}
            className={className}
            disabled={disabled}
          />
        }
        popperPlacement={position}
        portalId={portalId}
        onChange={handleChange}
        showYearPicker={showYearPicker}
      />
    </Wrapper>
  );
};

export default Datepicker;
